/**
 * Types for the Creative Studio
 */

// ─── Studio Configuration ───────────────────────────────────

export interface StudioConfig {
  session: {
    api_base: string;
    client_uid: string;
    mode: StudioMode;
  };
  client_info: {
    uid: string;
    name: string;
    category: string;
  };
  brand_tokens: BrandTokens;
  personas: Persona[];
  initial_data: {
    brief: Brief | null;
    persona_id: string | null;
    mode: StudioMode;
  };
  api_endpoints: {
    briefs: string;
    brief: string | null;
    personas: string;
    brand_tokens: string;
    skills_invoke: string;
  };
  delivery_mode: DeliveryMode;
  warnings?: string[];
}

export type StudioMode = 'workbench' | 'review';
export type DeliveryMode = 'mcp_app' | 'artifact' | 'data_only' | 'admin';

// ─── Brand Tokens ───────────────────────────────────────────

export interface BrandTokens {
  client_theme?: Record<string, string>;
  agency_theme?: {
    chrome_bg?: string;
    chrome_text?: string;
    logo_url?: string;
    name?: string;
    powered_by?: string;
  };
}

// ─── Persona ────────────────────────────────────────────────

export interface Persona {
  id?: string;
  persona_id?: string;
  name: string;
  description?: string;
  archetype?: string;
}

// ─── Brief ──────────────────────────────────────────────────

export interface Brief {
  brief_id: string;
  client_uid: string;
  name: string;
  description?: string;
  version: number;
  status: 'draft' | 'active' | 'archived' | 'superseded';
  persona_id?: string;
  playbook_ids?: string[];
  objective?: BriefSection;
  audience?: BriefSection;
  insight?: BriefSection;
  brand_voice?: BriefSection;
  message?: BriefSection;
  creative_context?: BriefSection;
  mandatories?: BriefSection;
  success_metrics?: BriefSection;
  channel_directives?: ChannelDirective[];
  product_brief?: Record<string, unknown>;
  campaign_context?: Record<string, unknown>;
  target_platforms?: string[];
  funnel_stages?: string[];
  tags?: string[];
  metadata?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

// ─── Ad Pack / Playbook ─────────────────────────────────────

export interface PlaybookSummary {
  playbook_id: string;
  name: string;
  campaign_name?: string;
  status: string;
  asset_summary?: {
    total_assets?: number;
    angles_count?: number;
    scripts_count?: number;
    images_count?: number;
    static_ads_count?: number;
    videos_count?: number;
  };
  pack_id?: string;
  has_zip?: boolean;
  cost_summary?: {
    total_cost_usd?: number;
  };
  created_at?: string;
  updated_at?: string;
}

export interface AdPackDownload {
  pack_id: string;
  download_url: string;
  expiry_hours: number;
}

export interface RankedAd {
  headline?: string;
  persona?: string;
  score?: number;
  image_url?: string;
  asset_id?: string;
}

export type BriefSection = Record<string, unknown>;

export interface ChannelDirective {
  channel: string;
  format_tiers?: FormatTier[];
  platform_notes?: string;
  metadata?: Record<string, unknown>;
}

export interface FormatTier {
  tier_name: string;
  formats: string[];
  aspect_ratio?: string;
  direction?: string;
}

// ─── Brief Section Keys ─────────────────────────────────────

export const BRIEF_SECTION_KEYS = [
  'objective',
  'audience',
  'insight',
  'brand_voice',
  'message',
  'creative_context',
  'mandatories',
  'success_metrics',
] as const;

export type BriefSectionKey = (typeof BRIEF_SECTION_KEYS)[number];

export const BRIEF_SECTION_LABELS: Record<BriefSectionKey, string> = {
  objective: 'Objective',
  audience: 'Audience',
  insight: 'Insight',
  brand_voice: 'Brand Voice',
  message: 'Message',
  creative_context: 'Creative Context',
  mandatories: 'Mandatories',
  success_metrics: 'Success Metrics',
};

// ─── Creative Angles ────────────────────────────────────────

export interface CreativeAngle {
  angle_id?: string;
  name?: string;
  angle_name?: string;
  hook?: string;
  message?: string;
  emotional_lever?: string;
  strategic_rationale?: string;
  archetype?: string;
  score?: number;
  format_guidance?: Record<string, unknown>;
}

// ─── Script ─────────────────────────────────────────────────

export interface UGCScript {
  title?: string;
  total_duration?: number;
  segments: ScriptSegment[];
  full_script?: string;
  hook_alternatives?: string[];
  cta_alternatives?: string[];
}

export interface ScriptSegment {
  segment_number?: number;
  duration?: number;
  visual_direction?: string;
  script_text?: string;
  delivery_notes?: string;
  b_roll_notes?: string;
}

// ─── Creative Review ────────────────────────────────────────

export interface CreativeReview {
  summary?: {
    verdict?: string;
    one_line?: string;
    strengths?: string[];
    gaps?: string[];
    overall_score?: number;
  };
  scores?: Record<string, number>;
  walkthrough?: WalkthroughEntry[];
  recommendations?: Recommendation[];
}

export interface WalkthroughEntry {
  moment?: string;
  reaction?: string;
  internal_monologue?: string;
  engagement_level?: number;
}

export interface Recommendation {
  title?: string;
  recommendation?: string;
  description?: string;
  priority?: string;
  impact?: string;
}

// ─── Pipeline ───────────────────────────────────────────────

export type StudioPanelId = 'brief' | 'angles' | 'script' | 'review' | 'assets';
export type PipelineStepStatus = 'pending' | 'active' | 'completed' | 'skipped';

export interface PipelineStep {
  id: StudioPanelId;
  label: string;
  status: PipelineStepStatus;
  skill_name: string;
  summary?: string;
}

// ─── Studio State ───────────────────────────────────────────

export interface StudioState {
  config: StudioConfig;
  clientUid: string;
  brief: Brief | null;
  briefVersion: number;
  angles: CreativeAngle[];
  anglesGenerating: boolean;
  anglesStale: boolean;
  script: UGCScript | null;
  scriptGenerating: boolean;
  scriptStale: boolean;
  review: CreativeReview | null;
  reviewStale: boolean;
  activePanel: StudioPanelId;
  loading: Record<string, boolean>;
  error: string | null;
  generationError: string | null;
  personas: Persona[];
  brandTokens: BrandTokens;
  pipelineStatus: PipelineStep[];
  // Actions
  setBrief: (brief: Brief | null) => void;
  setAngles: (angles: CreativeAngle[]) => void;
  setScript: (script: UGCScript | null) => void;
  setReview: (review: CreativeReview | null) => void;
  setActivePanel: (panel: StudioPanelId) => void;
  setLoading: (fn: (prev: Record<string, boolean>) => Record<string, boolean>) => void;
  setError: (error: string | null) => void;
  updateBrief: (updates: Partial<Brief>) => Promise<Brief | undefined>;
  refreshBrief: () => Promise<void>;
  generateAngles: () => Promise<void>;
  generateScript: (angle: CreativeAngle) => Promise<void>;
}
